import { removeQuotesFromText } from '@whistler-ui/k2/helpers/textHelpers';
import { getUserFacingErrorMessage } from '@whistler-ui/k2/helpers/userFacingError';
import { useAppMessages } from '@whistler-ui/k2/hooks/useAppMessages';
import {
  flattenObject,
  generateFormValuesMap,
  setDefaultValuesForSingleOptions,
} from '@whistler-ui/portal/components/TestDataPane/helpers';
import { API, USE_CASE } from '@whistler-ui/portal/constants/testConstants';
import { USE_CASE_API_TAGS } from '@whistler-ui/portal/constants/useCases';
import {
  useGetApisTestData,
  useGetUseCasesTestData,
} from '@whistler-ui/portal/hooks/queries/useUseCasesV2';
import {
  TUseCasesApiTags,
  TUseCaseTags,
} from '@whistler-ui/portal/types/tests';
import { TUseCaseType } from '@whistler-ui/portal/types/useCases';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface Params {
  tag: TUseCaseTags | TUseCasesApiTags;
  type: TUseCaseType;
  test: any;
  environmentId: string | undefined;
  hasQueryParams: boolean;
}

interface GenerateFormDataConfig {
  test: any;
  tag: TUseCaseTags | TUseCasesApiTags;
  hasQueryParams: boolean;
  additionalData?: any;
}

const NO_TEST_DATA_DEFINED = 'No test data defined';

// 1. FIXED: Use the actual constant reference, not the string name
const API_IGNORED_ERRORS = [
  NO_TEST_DATA_DEFINED,
  'NULL_ERROR',
  'TEST_DATA_ERROR',
];

// 2. FIXED: Added the constant here so Use Cases ignore it too
const USECASE_IGNORED_ERRORS = [
  NO_TEST_DATA_DEFINED,
  'NULL_ERROR',
  'TEST_DATA_ERROR',
  'VALIDATION_ERROR',
];

const defaultReturn = {
  data: undefined,
  isPending: false,
  error: null,
  isSuccess: false,
  formikData: {
    initialValues: {},
    rules: [],
    fields: {},
  },
  parameters: [],
};

const generateFormData = (config: GenerateFormDataConfig) => {
  const { test, hasQueryParams, additionalData } = config;

  const data = { fields: { parents: {} }, rules: [] };
  const formValuesMap = generateFormValuesMap(test.parameters, '', data);
  const formQueryValuesMap = hasQueryParams
    ? generateFormValuesMap(test.query_parameters, '', data)
    : {};

  const valuesMap = {
    ...formValuesMap,
    ...formQueryValuesMap,
    fields: data.fields,
  };

  if (additionalData) {
    const flatValues = flattenObject(additionalData, '', {}, hasQueryParams);
    const newValues = { ...valuesMap, ...flatValues, fields: data.fields };

    return {
      initialValues: newValues,
      rules: data.rules,
      fields: data.fields,
    };
  }

  return {
    initialValues: valuesMap,
    rules: data.rules,
    fields: data.fields,
  };
};

export const useGetTestData = ({
  tag,
  environmentId: envId,
  test,
  type,
  hasQueryParams,
}: Params) => {
  const { t } = useTranslation();
  const [setMessages] = useAppMessages(t);

  // 3. FIXED: Add a ref to track shown errors and prevent infinite toast loops
  const lastShownError = useRef<string | null>(null);

  const isApiType = type === API;
  const isUseCaseType = type === USE_CASE;

  const apiQuery = useGetApisTestData(envId, tag, test.tag, type);
  const useCaseQuery = useGetUseCasesTestData(envId, tag, test.tag, type);

  const activeQuery = isApiType
    ? apiQuery
    : isUseCaseType
    ? useCaseQuery
    : null;

  useEffect(() => {
    if (!activeQuery?.isError || !activeQuery.error?.message) return;

    const errorMsg = removeQuotesFromText(activeQuery.error.message);

    // Prevent showing the exact same error toast repeatedly for this component mount
    if (lastShownError.current === errorMsg) return;

    const ignoredErrors = isApiType
      ? API_IGNORED_ERRORS
      : USECASE_IGNORED_ERRORS;

    if (!ignoredErrors.includes(errorMsg)) {
      setMessages({
        type: useAppMessages.type.ERROR,
        msg: getUserFacingErrorMessage(
          activeQuery.error,
          'common:errors.default',
        ),
      });
      // Lock the error so it doesn't show again on re-renders
      lastShownError.current = errorMsg;
    }
  }, [
    activeQuery?.isError,
    activeQuery?.error?.message,
    isApiType,
    setMessages,
  ]);

  return useMemo(() => {
    if (!activeQuery) {
      return {
        ...defaultReturn,
        isApiType,
        isUseCaseType,
      };
    }

    const mapParameters = () => {
      if (
        hasQueryParams &&
        test.tag === USE_CASE_API_TAGS.ENTITLEMENT_INFO &&
        isApiType
      ) {
        const queryParams = test.query_parameters.map((qParam: any) => {
          if (qParam.name === 'oem') {
            return {
              ...qParam,
              options: qParam.options.map((item: string) => `${item} `),
            };
          }
          return qParam;
        });

        const entitlements = queryParams.pop();
        return [...test.parameters, entitlements, ...queryParams];
      }

      return hasQueryParams
        ? [...test.parameters, ...test.query_parameters]
        : test.parameters;
    };

    const parameters = mapParameters();

    if (activeQuery.isError) {
      if (
        removeQuotesFromText(activeQuery.error.message) === NO_TEST_DATA_DEFINED
      ) {
        const formData = generateFormData({
          test,
          tag,
          hasQueryParams,
        });
        const formDataWithDefaults = setDefaultValuesForSingleOptions(formData);

        return {
          ...activeQuery,
          formikData: formDataWithDefaults,
          parameters,
          isApiType,
          isUseCaseType,
        };
      }

      return {
        ...activeQuery,
        formikData: { initialValues: {}, rules: [], fields: {} },
        parameters,
        isApiType,
        isUseCaseType,
      };
    }

    if (
      activeQuery.isSuccess &&
      activeQuery.data &&
      Boolean(activeQuery.data?.id)
    ) {
      const formData = generateFormData({
        test,
        tag,
        hasQueryParams,
        additionalData: activeQuery.data.data,
      });

      const formDataWithDefaults = setDefaultValuesForSingleOptions(formData);

      return {
        ...activeQuery,
        formikData: formDataWithDefaults,
        parameters,
        isApiType,
        isUseCaseType,
      };
    }

    return {
      ...activeQuery,
      formikData: { initialValues: {}, rules: [], fields: {} },
      parameters: [],
      isApiType,
      isUseCaseType,
    };
  }, [activeQuery, test, tag, hasQueryParams, isApiType, isUseCaseType]);
};
